/* eslint-disable react/no-unknown-property */
/** @jsxImportSource @emotion/react */
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTheme } from '@emotion/react';
import Box from '@mui/material/Box';
import MuiButton from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Text from '@Text';
import FetchStatus from '../../Data/FetchStatus';
import { getClientApiSlotsAction } from '../../Hooks/actionCreators';
import { setSelectedSlot } from '../../Lib/redux/slices/clientApiJourney';
import { useWidgetHeightRefresh } from '../../Hooks/useWidgetHeightRefresh/useWidgetHeightRefresh';
import SlotGrid from './SlotGrid';
import makeSlotPickerStyles from './makeSlotPickerStyles';
import useClientApiCopy from './copy/useClientApiCopy';

const SPINNER_DELAY_MS = 200;
// Matches the expand/collapse grid-template-rows transition in makeSlotPickerStyles.
const EXPAND_TRANSITION_MS = 350;

/**
 * Collapsible nearby-store row: lazily fetches its own slots on first expand
 * and only shows a spinner if that fetch is still pending after 200ms, to
 * avoid flashing a loader for fast/cached responses.
 */
const NearbyBranch = ({
  location,
  isExpanded,
  onOpen,
  namePrefix = '',
  locationStepKey,
}) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const classes = makeSlotPickerStyles({ theme });
  const copy = useClientApiCopy();

  const slotState = useSelector(
    (store) => store.clientApiJourney.location?.slotsByLocation?.[location.id]
  );
  const selectedSlot = useSelector(
    (store) => store.clientApiJourney.appointment?.selectedSlot
  );

  const slotsStatus = slotState?.slotsStatus;
  const [showSpinner, setShowSpinner] = useState(false);
  const spinnerTimer = useRef(null);

  useWidgetHeightRefresh([slotsStatus, showSpinner]);
  // Re-check the widget height after the expand/collapse animation settles so
  // the widget grows/shrinks to the new height (measuring it mid-transition
  // would leave it stuck — e.g. not shrinking when a card collapses).
  useWidgetHeightRefresh([isExpanded], EXPAND_TRANSITION_MS + 50);

  useEffect(() => {
    if (!isExpanded) {
      clearTimeout(spinnerTimer.current);
      setShowSpinner(false);
      return undefined;
    }

    const hasSlots = slotsStatus === FetchStatus.COMPLETED;
    const isInProgress = slotsStatus === FetchStatus.IN_PROGRESS;

    // Opening a card re-lists its clinic so the server's latest selection matches
    // what the user sees. A loaded card refreshes (clearing stale slots and its
    // pick); a failed card retries (a transient failure may clear); an
    // in-progress one already has a list on the way.
    if (hasSlots) {
      dispatch(
        getClientApiSlotsAction({ location, locationStepKey, refresh: true })
      );
    } else if (!isInProgress) {
      dispatch(getClientApiSlotsAction({ location, locationStepKey }));
    }

    spinnerTimer.current = setTimeout(
      () => setShowSpinner(true),
      SPINNER_DELAY_MS
    );
    return () => clearTimeout(spinnerTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExpanded]);

  useEffect(() => {
    if (
      slotsStatus === FetchStatus.COMPLETED ||
      slotsStatus === FetchStatus.FAILED
    ) {
      clearTimeout(spinnerTimer.current);
      setShowSpinner(false);
      return undefined;
    }

    // A re-list of an already-loaded card flips the status to IN_PROGRESS after
    // mount, so arm the spinner here too (the expand effect only runs on expand).
    if (slotsStatus === FetchStatus.IN_PROGRESS && isExpanded) {
      spinnerTimer.current = setTimeout(
        () => setShowSpinner(true),
        SPINNER_DELAY_MS
      );
      return () => clearTimeout(spinnerTimer.current);
    }

    return undefined;
  }, [slotsStatus, isExpanded]);

  const handleSelectSlot = (selected) => {
    dispatch(setSelectedSlot({ selectedSlot: selected }));
  };

  return (
    <Box
      css={[
        classes.nearbyCard,
        isExpanded ? classes.nearbyCardOpen : classes.nearbyCardClosed,
      ]}
      // Clicking the collapsed card opens it, as a mouse convenience. It is not a
      // button: the keyboard and screen-reader control is the "view all" button
      // inside, so the card stays plain content and reads cleanly.
      onClick={isExpanded ? undefined : () => onOpen(location.id)}
      data-testid={`nearbyBranch-${location.id}`}
    >
      <Box css={classes.nearbyHeader}>
        <Text variant="text">
          {namePrefix}
          <span css={classes.storeNameBold}>{location.title}</span>
        </Text>
        <Box
          css={[
            classes.distanceBadge,
            !isExpanded && classes.distanceBadgeMuted,
          ]}
        >
          <Text variant="text">
            {location.distance} {location.distanceUnit}
          </Text>
        </Box>
      </Box>
      <Box css={classes.nearbyAddress}>
        <Text variant="text">
          <svg
            css={classes.addressPin}
            xmlns="http://www.w3.org/2000/svg"
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0" />
            <circle cx="12" cy="10" r="3" />
          </svg>
          {location.address}
        </Text>
      </Box>
      {!isExpanded && (
        <Box css={classes.toggleLinkContainer}>
          <MuiButton
            css={[classes.toggleLink, classes.toggleLinkMuted]}
            onClick={(event) => {
              event.stopPropagation();
              onOpen(location.id);
            }}
            data-testid={`nearbyBranchToggle-${location.id}`}
          >
            {copy.slot.viewAll}
          </MuiButton>
        </Box>
      )}
      <Box css={[classes.expandable, isExpanded && classes.expandableOpen]}>
        <Box css={classes.expandableInner}>
          {showSpinner && (
            <Box
              css={classes.message}
              data-testid={`nearbyBranchSpinner-${location.id}`}
            >
              <CircularProgress size={20} />
            </Box>
          )}
          {!showSpinner && slotsStatus === FetchStatus.FAILED && (
            <Box
              css={classes.message}
              data-testid={`nearbyBranchFailed-${location.id}`}
            >
              <Text variant="text">{copy.slot.fetchFailed}</Text>
            </Box>
          )}
          {!showSpinner && slotsStatus === FetchStatus.COMPLETED && (
            <Box
              css={classes.nearbySlots}
              data-testid={`nearbyBranchSlots-${location.id}`}
            >
              <SlotGrid
                locationId={location.id}
                slots={slotState?.slots}
                selectedSlot={selectedSlot}
                onSelectSlot={handleSelectSlot}
              />
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default NearbyBranch;
